import React, { useEffect } from "react"
import { PropTypes } from "prop-types"

import { getAssetPath } from "@commonUtils"

import ImageLoader from "@components/ImageLoader/ImageLoader"
import Separator from "@components/Separator/Separator"
import ARIAButton from "@wrappers/ARIAButton/ARIAButton"

import css from "./Dialog.scss"

const crossIcon = getAssetPath("svgs/icons/cross_24px_primary.svg")
const backIcon = getAssetPath("svgs/icons/arrow-left_24px_primary.svg")

const Dialog = ({
    isOpen,
    header,
    showBackIcon,
    showCloseIcon,
    hasSeparatorHeader = true,
    hasSeparatorFooter = true,
    children,
    onCloseClick,
    onBackClick,
    onOverlayClick,
    stickyBottomComponent,
    addTopHeight = true,
    shouldAddChildPadding = true,
    hasBgImage = false,
}) => {
    useEffect(() => {
        // This prevents scrolling of the page when user scrolls in the Dialog box
        // https://css-tricks.com/prevent-page-scrolling-when-a-modal-is-open
        const body = document.body
        if (isOpen) {
            const scrollY = `${window.scrollY}px`
            body.style.position = "fixed"
            body.style.inset = `-${scrollY} 0 0 0`
        } else {
            if (body.style.position === "fixed") {
                // Undo the body styles and scroll the window to current position (Otherwise it just scrolls to top)
                const scrollY = body.style.top
                body.style.position = ""
                body.style.inset = ""
                // Set scroll-behavior of html element to "auto" (to avoid any scroll animation)
                const scrollingElement = document.documentElement
                scrollingElement.style.scrollBehavior = "auto"
                window.scrollTo(0, parseInt(scrollY || "0") * -1)
                scrollingElement.style.scrollBehavior = ""
            }
        }
    }, [isOpen])

    const hasBgImageHeaderStyle = hasBgImage ? { display: "none" } : {}
    return (
        <div
            onClick={onOverlayClick}
            className={`flex alignCenter justifyCenter ${css.overlay} ${isOpen ? css.open : css.close}`}
            role="dialog"
            aria-modal="true"
        >
            <div className={css.container} onClick={(e) => e.stopPropagation()}>
                {showBackIcon || header || showCloseIcon ? (
                    <div className={css.stickyTop}>
                        <div className={`${css.header} padding-0-24 flex alignCenter justifyBetween`}>
                            <div className="flex alignCenter">
                                {showBackIcon && (
                                    <div className="gutter-only">
                                        <ARIAButton onClick={onBackClick} ariaLabel="back">
                                            <ImageLoader
                                                height={24}
                                                width={24}
                                                alt={"back"}
                                                src={backIcon}
                                                className="pointer"
                                            />
                                        </ARIAButton>
                                    </div>
                                )}

                                {header && (
                                    <div className={`l3SemiBold gutter-only ${css.lineClamp1} `}>
                                        {header}
                                    </div>
                                )}
                            </div>
                            {showCloseIcon && (
                                <div className="gutter-only">
                                    <ARIAButton onClick={onCloseClick} ariaLabel="cross">
                                        <ImageLoader
                                            height={24}
                                            width={24}
                                            alt={"cross"}
                                            src={crossIcon}
                                            className={`pointer ${header ? css.marginLeft8 : ""}`}
                                        />
                                    </ARIAButton>
                                </div>
                            )}
                        </div>
                        {hasSeparatorHeader && <Separator size="xs" />}
                    </div>
                ) : addTopHeight ? (
                    <div className={`${css.blockOf24} ${css.stickyTop}`} style={hasBgImageHeaderStyle} />
                ) : null}
                <div className={`${shouldAddChildPadding ? "padding-0-24" : ""}`}>{children}</div>
                {stickyBottomComponent && (
                    <div className={css.stickyBottom}>
                        {hasSeparatorFooter && <Separator size="xs" />}
                        <div className={`${css.footer} padding-0-24 flex alignCenter justifyBetween`}>
                            {stickyBottomComponent}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

Dialog.propTypes = {
    isOpen: PropTypes.bool,
    header: PropTypes.string,
    showBackIcon: PropTypes.bool,
    showCloseIcon: PropTypes.bool,
    hasSeparatorHeader: PropTypes.bool,
    hasSeparatorFooter: PropTypes.bool,
    children: PropTypes.node,
    onCloseClick: PropTypes.func,
    onBackClick: PropTypes.func,
    onOverlayClick: PropTypes.func,
    stickyBottomComponent: PropTypes.node,
    addTopHeight: PropTypes.bool,
    shouldAddChildPadding: PropTypes.bool,
    hasBgImage: PropTypes.bool,
}

export default Dialog
